import styled, { css, } from 'styled-components';
import { injectTypography } from '../../utils/injectTypography';
import { injectInteractionBar, injectInteractionBarActiveStyles, injectInteractionBarHoverStyles, interactionBarSelector, } from '../../utils/injectInteractionBar';
import { injectSpacing } from '../../api/spacing/injectSpacing';
import { getEasing } from '../../utils/getEasing';
import { injectOSXFontSmoothing } from '../../utils/injectOSXFontSmoothing';
var GridAreas;
(function (GridAreas) {
    GridAreas["label"] = "Label";
    GridAreas["input"] = "Input";
    GridAreas["extensionEnd"] = "ExtensionEnd";
})(GridAreas || (GridAreas = {}));
function injectInputLayout(props) {
    const layout = {
        gridTemplateAreas: `
      "${GridAreas.label}"
      "${GridAreas.input}"
    `,
    };
    if (props.hasExtensionEnd) {
        layout.gridTemplateAreas = `
      "${GridAreas.label} X"
      "${GridAreas.input} ${GridAreas.extensionEnd}"
    `;
        layout.gridTemplateColumns = '1fr auto';
    }
    return layout;
}
const getStateColor = ({ theme, disabled, invalid, readOnly, }, state) => {
    const key = state ||
        (disabled && 'disabled') ||
        (invalid && 'invalid') ||
        (readOnly && 'readOnly') ||
        'default';
    return {
        default: theme.colors.form.primary,
        disabled: theme.colors.form.secondary,
        invalid: theme.colors.form.error,
        readOnly: theme.colors.form.primary,
        hover: theme.colors.form.strong,
    }[key];
};
/**
 * https://styled-components.com/docs/api#shouldforwardprop
 *
 * @param {(string | number)} prop
 * @param {((prop: string | number) => boolean)} defaultValidatorFn
 * @returns
 */
function filterProps(prop, defaultValidatorFn) {
    const list = ['disabled'];
    return !list.includes(prop) && defaultValidatorFn(prop);
}
const commonStyles = (props) => css `
  margin: 0;
  padding: 0;

  color: ${getStateColor};
  ${injectTypography(props, 'copy1')}
  ${injectOSXFontSmoothing(true)}
  
  background-color: transparent;
  border: none;
  border-radius: 0;

  appearance: none;

  &:disabled {
    color: ${getStateColor(props, 'disabled')};

    cursor: not-allowed;
  }
`;
export const StyledTextField = styled.div `
  ${injectSpacing('margin')}
`;
export const StyledTextFieldLayout = styled.div.withConfig({
    shouldForwardProp: filterProps,
}) `
  --text-field-indent-block-start: var(
    ${(props) => props.theme.responsive.spacing.xxs}
  );
  --text-field-indent-block-end: var(
    ${(props) => props.theme.responsive.spacing.xs}
  );
  --text-field-extension-indent: var(
    ${(props) => props.theme.responsive.spacing.xs}
  );

  display: grid;

  ${injectInputLayout}
`;
export const StyledInputWrapper = styled.div.withConfig({
    shouldForwardProp: filterProps,
}) `
  /** 
   * This container is only here to give context to some CSS selectors like
   *':focus-within' and '::after'.
   */
  display: contents;

  ${(props) => !props.readOnly &&
    css `
      ${injectInteractionBar(props)}

      &${interactionBarSelector} {
        grid-area: ${GridAreas.input};
        align-self: end;
      }
    `}
`;
export const StyledExtension = styled.div `
  ${commonStyles}

  position: relative;

  display: inline-block;
  grid-area: ${GridAreas.extensionEnd};
  box-sizing: content-box;
  min-width: 24px;
  max-height: 25px; /* includes 1px for the bottom border */
  padding-block-start: var(--text-field-indent-block-start);
  padding-block-end: var(--text-field-indent-block-end);
  padding-inline-start: var(--text-field-extension-indent);
  padding-inline-end: var(--text-field-extension-indent);

  text-align: end;

  cursor: ${(props) => props.onClick && 'pointer'};

  user-select: none;

  ${(props) => props.onClick &&
    css `
      &:hover {
        color: ${getStateColor(props, 'hover')};
      }
    `}

  ${(props) => !props.readOnly &&
    css `
      ${injectInteractionBar({ ...props, static: !props.onClick })}

      &${interactionBarSelector} {
        position: absolute;
        bottom: 0;
        left: 0;
      }

      /* Trigger interactionBar when preceding elements child received focus */
      *:focus-within ~ &${interactionBarSelector} {
        ${injectInteractionBarActiveStyles}
      }

      /* Trigger interactionBar when preceding elements child received hover */
      *:hover ~ &${interactionBarSelector} {
        ${injectInteractionBarHoverStyles}
      }
    `}
`;
export const StyledInput = styled.input `
  ${commonStyles}

  display: block;
  grid-area: ${GridAreas.input};
  box-sizing: border-box;
  width: 100%;

  padding-inline-start: 2px; /* Make sure the cursor is visible next to outline on focus */
  padding-inline-end: var(--text-field-extension-indent);
  padding-block-start: var(--text-field-indent-block-start);
  padding-block-end: var(--text-field-indent-block-end);
  margin-block-end: 1px; /* Adds the space for the interaction bar */

  transition: color 0.25s ${getEasing};

  &:hover {
    color: ${(props) => getStateColor(props, 'hover')};
  }

  &:invalid {
    box-shadow: none; /* Removes outline in Firefox */
  }
`;
//# sourceMappingURL=TextField.styles.js.map